import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Col, Divider, Form, Input, message, Modal, Row} from "antd";
import TextArea from "antd/es/input/TextArea";
import PhoneInput from "antd-phone-input";
import {Country} from "../../../../../models/Country";
import {CompanyService} from "../../../../../services/CompanyService";
import {FileService} from "../../../../../services/FileService";
import {CompanyContactService} from "../../../../../services/CompanyContactService";
import {CompanyContact} from "../../../../../models/CompanyContact";

interface DetailContactModalProps {
    isOpen: boolean;
    id: number;
    handleModalCancel: () => void;
    countryCode: string;
    countries: Country[];
    submit: (id: number, name: string, lastname: string, email: string, dialCode: string, phoneNumber: string, description: string, isDefaultContact: boolean) => void;
}

function DetailContactModal({isOpen, handleModalCancel, id, countryCode, countries, submit}: DetailContactModalProps) {
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);
    const [contact, setContact] = useState<CompanyContact>();

    useEffect(() => {
        form.resetFields();

        const country = findCountryByCode(countryCode);

        if(country) {
            form.setFieldValue('phone', {...form.getFieldValue('phone'), countryCode: parseInt(country.dialCode)})
        }

        if(isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const fetchData = async () => {
        setLoading(true);

        const response = await CompanyContactService.findById(id);

        if(response.success) {
            setContact(response.data);

            form.setFieldValue('name', response.data.name);
            form.setFieldValue('lastname', response.data.lastname);
            form.setFieldValue('email', response.data.email);
            form.setFieldValue('description', response.data.description || '');
            form.setFieldValue('isDefaultContact', response.data.isDefaultContact);
            let newPhone = {...form.getFieldValue('phone'), countryCode: parseInt(response.data.dialCode), areaCode: null, phoneNumber: null};

            if(response.data.phoneNumber) {
                newPhone.areaCode = response.data.phoneNumber;
            }

            form.setFieldValue('phone', newPhone);
        }else {
            const error = response.data;
            messageApi.error(error.message as string || 'Hubo un error al intentar obtener los datos del contacto, por favor inténtalo nuevamente.', 3.5);
            handleModalCancel();
        }

        setLoading(false);
    }

    const onFinish = async (values: any) => {
        setLoading(true);
        await submit(
            id,
            values.name,
            values.lastname,
            values.email,
            `+${values.phone.countryCode}`,
            `${values.phone.areaCode}${values.phone.phoneNumber}`,
            values.description || '',
            !!values.isDefaultContact
        );
        setLoading(false);
    }

    const findCountryByCode = (code: string) => {
        return countries.find((record) => {
            return record.code === code;
        });
    }

    return (
        <>
            {contextHolder}

            <Modal
                title={`Ver contacto [ID: ${id}]`}
                open={isOpen}
                onCancel={handleModalCancel}
                maskClosable={false}
                destroyOnClose
                footer={null}
                width={640}
            >
                <Divider style={{ marginTop: '15px', marginBottom: '15px' }}/>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Row gutter={24}>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                name="name"
                                label="Nombres"
                                rules={[{ required: true, message: 'Debes de ingresar los nombres.' }]}
                            >
                                <Input type="text" disabled={loading} placeholder="Ingrese los nombres"/>
                            </Form.Item>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Form.Item
                                name="lastname"
                                label="Apellidos"
                                rules={[{ required: true, message: 'Debes de ingresar los apellidos.' }]}
                            >
                                <Input type="text" disabled={loading} placeholder="Ingrese los apellidos" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col xs={24} lg={24}>
                            <Form.Item
                                name="email"
                                label="Correo electrónico"
                                rules={[
                                    { required: true, message: 'Debes ingresar el correo electrónico.' },
                                    {
                                        type: 'email',
                                        message: 'El correo electrónico no es válido.'
                                    }
                                ]}
                            >
                                <Input
                                    type="text"
                                    disabled={loading}
                                    placeholder="Ingrese el correo electrónico"
                                    addonAfter={'@'}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24}>
                            <Form.Item
                                name="phone"
                                label="Teléfono"
                                rules={[
                                    { required: true, message: 'El teléfono es requerido' },
                                    {
                                        validator: (_, value) => {
                                            if (value && value.valid()) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Debes ingresar un número de teléfono válido.'));
                                        },
                                    },
                                ]}
                            >
                                <PhoneInput enableSearch searchNotFound="No se encontró el país" searchPlaceholder="Buscar país" allowClear placeholder="Ingrese el teléfono" disabled={loading}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24} lg={24}>
                            <Form.Item
                                name="description"
                                label="Descripción"
                            >
                                <TextArea rows={2} placeholder="Ingrese una descripción" disabled={loading}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item
                                name="isDefaultContact"
                                valuePropName="checked"
                            >
                                <Checkbox disabled={loading}>Contacto principal</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24}>
                            <Button onClick={() => { form.submit(); }} loading={loading} type="primary" block>Guardar cambios</Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
}

export default DetailContactModal;
